// src/App.tsx
import { useOnboarding } from './onboarding/OnboardingCoach';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import { motion, AnimatePresence } from 'framer-motion';
import MobileBottomNav from './components/MobileBottomNav';
import { isRunningAsApp, markAppAsInstalled } from './utils/appMode';

// ✅ Gentle email verification reminder (shows once per hour)
import EmailVerificationReminder from './components/EmailVerificationReminder';

// ✅ Onboarding providers
import { AppSheetsProvider } from './onboarding/AppSheets';
import { OnboardingCoachProvider } from './onboarding/OnboardingCoach';

// ✅ Overlay context for managing modals/popups
import { OverlayProvider } from './context/OverlayContext';

// ✅ Scroll to top on route change
import ScrollToTop from './components/ScrollToTop';

// ✅ Profile completion guard
import ProfileCompleteGuard from './components/ProfileCompleteGuard';

// Lazy load page components for better performance
const EventsPage = lazy(() => import('./pages/EventsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MyEventsPage = lazy(() => import('./pages/MyEventsPage'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const MyProfilePage = lazy(() => import('./pages/MyProfilePage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const SafetyCenter = lazy(() => import('./pages/SafetyCenter'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const VerificationFlowPage = lazy(() => import('./pages/VerificationFlowPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminSeededVybesPage = lazy(() => import('./pages/AdminSeededVybesPage'));
const AdminAdBannerPage = lazy(() => import('./pages/AdminAdBannerPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelledPage = lazy(() => import('./pages/PaymentCancelledPage'));
const EmailPendingPage = lazy(() => import('./pages/EmailPendingPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'));
const TribePage = lazy(() => import('./pages/TribePage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const MagicLinkCallback = lazy(() => import('./pages/MagicLinkCallback'));
const WelcomeRedirect = lazy(() => import('./pages/WelcomeRedirect'));
const EmailRedirect = lazy(() => import('./pages/EmailRedirect'));
const OpenInApp = lazy(() => import('./pages/OpenInApp'));

// Lazy load components
const ConfirmationSuccess = lazy(() => import('./components/ConfirmationSuccess'));
const ResetPasswordForm = lazy(() => import('./components/ResetPasswordForm'));
const EventDetailsRouteWrapper = lazy(() => import('./components/EventDetailsRouteWrapper'));
const UserProfileView = lazy(() => import('./components/UserProfileView'));
const CreateEventModal = lazy(() => import('./components/CreateEventModal'));
const FirstLaunchExplainerModal = lazy(() => import('./components/FirstLaunchExplainerModal'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen bg-theme-background text-theme-text-main">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-theme-primary"></div>
  </div>
);

const ProtectedRoutes = () => {
  const {
    isAuthenticated,
    currentUser,
    isLoading,
    hasHydrated,
    sessionChecked,
    previewActive,     // 1-hour preview flag
    isVerified,        // function
  } = useAuth();

  // canBrowse = logged in OR in 1-hour preview
  const canBrowse = isAuthenticated || previewActive;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activePage, setActivePage] = useState('home');
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [showFirstLaunchModal, setShowFirstLaunchModal] = useState(false);

  const { ensure, missing } = useOnboarding();

  // 🔧 Adjust how often we re-prompt after a skip.
  const REMINDER_HOURS = 24;
  const PHOTO_REMINDER_HOURS = 1; // Photo prompts every 1 hour until completed

  // Scope localStorage keys per-user
  const uid = currentUser?.id || 'anon';
  const key = (base: string) => `${base}_${uid}`;
  const KEY_DONE = key('onb_bootstrap_done_v1');
  const KEY_NEXT = key('onb_next_bootstrap_at');
  const KEY_PHOTO_NEXT = key('onb_next_photo_at');
  const KEY_TRIGGER_USER = key('onb_bootstrap_trigger_v1');
  const KEY_TRIGGER_GLOBAL = 'onb_bootstrap_trigger_v1';

  useEffect(() => {
    console.log('ProtectedRoutes: Auth state:', {
      isAuthenticated,
      currentUser: currentUser ? { id: currentUser.id, username: currentUser.username, email: currentUser.email } : null,
      isLoading,
      hasHydrated,
      sessionChecked,
      previewActive,
      verified: isVerified(),
      canBrowse,
    });
    console.log('ProtectedRoutes: localStorage preview:', localStorage.getItem('preverify_expires_at'));
  }, [isAuthenticated, currentUser?.id, isLoading, hasHydrated, sessionChecked, previewActive]);

  // Mark app as installed when running in app mode (helps with detection)
  useEffect(() => {
    if (isRunningAsApp()) {
      markAppAsInstalled();
      console.log('ProtectedRoutes: Running in app mode');
    }
  }, []);

  // ✅ Progressive onboarding bootstrap + re-prompt scheduling
  // Note: Location prompt has been removed from auto-trigger. Users can enable via navbar or filters.
  // DISABLED: Auto-popups removed per user request
  useEffect(() => {
    if (
      false && // DISABLED - no auto popups
      import.meta.env.VITE_ONBOARDING_JOURNEY === '1' &&
      isAuthenticated &&
      currentUser &&
      currentUser.profileStatus && // wait for normalized profile
      missing.some(f => ['photo', 'interests'].includes(f))
    ) {
      const now = Date.now();
      const nextAt = Number(localStorage.getItem(KEY_NEXT) || '0');
      const photoNextAt = Number(localStorage.getItem(KEY_PHOTO_NEXT) || '0');
      const done = localStorage.getItem(KEY_DONE) === '1';
      const trigger =
        localStorage.getItem(KEY_TRIGGER_USER) === '1' ||
        localStorage.getItem(KEY_TRIGGER_GLOBAL) === '1';

      // Check which fields to prompt for (location removed from auto-prompt)
      const missingPhoto = missing.includes('photo');
      const missingOther = missing.includes('interests');

      // Determine if we should run based on what's missing
      const shouldRunPhoto = missingPhoto && (trigger || now >= photoNextAt);
      const shouldRunOther = missingOther && (trigger || !done || now >= nextAt);
      const shouldRun = shouldRunPhoto || shouldRunOther;

      if (shouldRun) {
        // consume triggers
        localStorage.removeItem(KEY_TRIGGER_USER);
        localStorage.removeItem(KEY_TRIGGER_GLOBAL);

        (async () => {
          try {
            // Build the fields to ensure based on timing
            const fieldsToEnsure: ('photo' | 'interests')[] = [];
            if (shouldRunPhoto) fieldsToEnsure.push('photo');
            if (shouldRunOther) {
              if (missing.includes('interests')) fieldsToEnsure.push('interests');
            }

            const completed = await ensure(fieldsToEnsure);

            // Update timing for next prompts
            if (completed) {
              localStorage.setItem(KEY_DONE, '1');
              localStorage.removeItem(KEY_NEXT);
              localStorage.removeItem(KEY_PHOTO_NEXT);
            } else {
              // If photo was in the sequence and still missing, schedule next photo prompt in 1 hour
              if (fieldsToEnsure.includes('photo') && missing.includes('photo')) {
                const nextPhotoTime = now + PHOTO_REMINDER_HOURS * 60 * 60 * 1000;
                localStorage.setItem(KEY_PHOTO_NEXT, String(nextPhotoTime));
              }

              // If other fields were in sequence, schedule next general prompt in 24 hours
              if (shouldRunOther) {
                localStorage.setItem(KEY_DONE, '0');
                const nextTime = now + REMINDER_HOURS * 60 * 60 * 1000;
                localStorage.setItem(KEY_NEXT, String(nextTime));
              }
            }
          } catch (e) {
            console.error('Onboarding bootstrap failed:', e);
            if (missing.includes('photo')) {
              const nextPhotoTime = now + PHOTO_REMINDER_HOURS * 60 * 60 * 1000;
              localStorage.setItem(KEY_PHOTO_NEXT, String(nextPhotoTime));
            }
            const nextTime = now + REMINDER_HOURS * 60 * 60 * 1000;
            localStorage.setItem(KEY_NEXT, String(nextTime));
          }
        })();
      }
    }
  }, [isAuthenticated, currentUser?.id, currentUser?.profileStatus, missing, ensure]);

  // Reset location prompt state when user changes or logs out

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if we should show the first launch explainer modal
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id) {
      setShowFirstLaunchModal(false);
      return;
    }

    const storageKey = `vybetribe_first_launch_seen_${currentUser.id}`;
    const hasSeenModal = localStorage.getItem(storageKey);

    if (!hasSeenModal) {
      const timer = setTimeout(() => {
        setShowFirstLaunchModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, currentUser?.id]);

  const handleCloseFirstLaunchModal = () => {
    if (currentUser?.id) {
      const storageKey = `vybetribe_first_launch_seen_${currentUser.id}`;
      localStorage.setItem(storageKey, 'true');
    }
    setShowFirstLaunchModal(false);
  };

  // Wire event listener for create vybe action
  useEffect(() => {
    const openCreateVybe = () => setShowCreateEventModal(true);
    document.addEventListener('open-create-vybe', openCreateVybe);
    return () => {
      document.removeEventListener('open-create-vybe', openCreateVybe);
    };
  }, []);

  // Load unread messages count for mobile bottom nav
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id) {
      setUnreadMessagesCount(0);
      return;
    }

    const loadCounts = async () => {
      try {
        const { supabase } = await import('./lib/supabaseClient');

        // Load unread messages count
        const { count: msgCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', currentUser.id)
          .eq('read', false);

        setUnreadMessagesCount(msgCount || 0);
      } catch (error) {
        console.error('Error loading counts:', error);
      }
    };

    loadCounts();

    // Set up real-time subscription
    const setupSubscriptions = async () => {
      const { supabase } = await import('./lib/supabaseClient');

      const messagesChannel = supabase
        .channel('mobile-messages-' + currentUser.id)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${currentUser.id}`,
          },
          () => loadCounts()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(messagesChannel);
      };
    };

    setupSubscriptions();
  }, [isAuthenticated, currentUser?.id]);


  // Basic guard while auth session hydrates
  if (isLoading || !hasHydrated || !sessionChecked) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-theme-background text-theme-text-main">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-theme-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mobile-full-height bg-theme-background text-theme-text-main font-inter flex flex-col">
      {/* ✅ Gentle email verification reminder (once per hour) */}
      {isAuthenticated && !isVerified() && currentUser?.email && (
        <EmailVerificationReminder email={currentUser.email} userId={currentUser.id} />
      )}

      <Navbar
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onStartVybe={() => setShowCreateEventModal(true)}
      />

      <div className="flex flex-1 pt-[56px]">
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-theme-background bg-opacity-80 z-[50] lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween', duration: 0.3 }}
                className="fixed top-[56px] bottom-0 left-0 w-64 shadow-lg z-[51] lg:hidden overflow-y-auto border-r border-theme-cards bg-theme-cards"
              >
                <Sidebar
                  isOpen={sidebarOpen}
                  onClose={() => setSidebarOpen(false)}
                  setActivePage={setActivePage}
                  activePage={activePage}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <div className="hidden lg:block w-64 min-h-screen border-r border-theme-cards bg-theme-cards">
          <Sidebar
            isOpen={true}
            onClose={() => {}}
            setActivePage={setActivePage}
            activePage={activePage}
          />
        </div>

        <main className="flex-1 p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden bg-theme-background text-theme-text-main pb-20 md:pb-3">
          {/* Use previewActive to allow read-only browsing */}
          <ProfileCompleteGuard>
            <Suspense fallback={<PageLoader />}>
              <Routes key={canBrowse ? 'can-browse' : 'locked'}>
              {/* Home: App mode always redirects, browser redirects if profile complete */}
              <Route
                path="/"
                element={
                  isRunningAsApp()
                    ? <Navigate to="/vybes" replace />
                    : (isAuthenticated && currentUser?.full_name && currentUser?.interests && currentUser.interests.length > 0)
                    ? <Navigate to="/vybes" replace />
                    : <LandingPage />
                }
              />

              {/* Legal & static */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/safety" element={<SafetyCenter />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Email confirm success (legacy) */}
              <Route path="/auth/confirm" element={<ConfirmationSuccess />} />
              {/* NEW: Supabase magic-link / PKCE callback */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Custom magic link callback */}
              <Route path="/auth/magic" element={<MagicLinkCallback />} />
              {/* Welcome email redirect */}
              <Route path="/welcome" element={<WelcomeRedirect />} />
              {/* Email link redirect handler */}
              <Route path="/go" element={<EmailRedirect />} />
              {/* Open in app bridge route (for email magic links) */}
              <Route path="/open" element={<OpenInApp />} />
              {/* Email verification via custom token */}
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* Login/Register */}
              <Route path="/login" element={<LoginPage />} />

              {/* Misc */}
              <Route path="/email-pending" element={<EmailPendingPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/complete-profile" element={isAuthenticated ? <CompleteProfilePage /> : <Navigate to="/login" />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/verify/start" element={isAuthenticated ? <VerificationFlowPage /> : <Navigate to="/login" />} />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
              <Route path="/subscription-success" element={<PaymentSuccessPage />} />
              <Route path="/subscription-cancelled" element={<PaymentCancelledPage />} />

              {/* Main app routes:
                  - /vybes is publicly accessible (logged-in and logged-out users can browse)
                  - /events redirects to /vybes for consistency
              */}
              <Route path="/vybes" element={<EventsPage />} />
              <Route path="/vybes/:eventId" element={<EventDetailsRouteWrapper />} />
              <Route path="/events" element={<Navigate to="/vybes" replace />} />
              <Route path="/events/:eventId" element={<EventDetailsRouteWrapper />} />

              {/* Auth-required pages (no preview) */}
              <Route path="/myvybes" element={isAuthenticated ? <MyEventsPage /> : <Navigate to="/" />} />
              <Route path="/myEvents" element={<Navigate to="/myvybes" replace />} />
              <Route path="/myvyberequests" element={isAuthenticated ? <ApplicationsPage /> : <Navigate to="/" />} />
              <Route path="/applications" element={<Navigate to="/myvyberequests" replace />} />
              <Route path="/myAccount" element={isAuthenticated ? <MyProfilePage /> : <Navigate to="/" />} />
              <Route path="/messages" element={isAuthenticated ? <MessagesPage /> : <Navigate to="/" />} />
              <Route path="/notifications" element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/" />} />
              <Route path="/tribe" element={isAuthenticated ? <TribePage /> : <Navigate to="/" />} />
              <Route path="/users" element={isAuthenticated ? <TribePage /> : <Navigate to="/" />} />
              <Route path="/users/:userId" element={isAuthenticated ? <UserProfileView /> : <Navigate to="/" />} />
              <Route path="/favorites" element={isAuthenticated ? <FavoritesPage /> : <Navigate to="/" />} />
              <Route path="/admin" element={isAuthenticated ? <AdminPage /> : <Navigate to="/" />} />
              <Route path="/admin/seeded-vybes" element={isAuthenticated ? <AdminSeededVybesPage /> : <Navigate to="/" />} />
              <Route path="/admin/ad-banner" element={isAuthenticated ? <AdminAdBannerPage /> : <Navigate to="/" />} />
              </Routes>
            </Suspense>
          </ProfileCompleteGuard>
        </main>
      </div>

      <Footer />

      {/* Global Create Event Modal */}
      {isAuthenticated && showCreateEventModal && (
        <Suspense fallback={null}>
          <CreateEventModal
            isOpen={showCreateEventModal}
            onClose={() => setShowCreateEventModal(false)}
            onEventCreated={() => {
              setShowCreateEventModal(false);
            }}
          />
        </Suspense>
      )}

      {/* Mobile Bottom Nav */}
      {isAuthenticated && (
        <MobileBottomNav
          unreadMessages={unreadMessagesCount}
          onCreateVybe={() => setShowCreateEventModal(true)}
        />
      )}

      {/* First Launch Explainer Modal */}
      {isAuthenticated && showFirstLaunchModal && (
        <Suspense fallback={null}>
          <FirstLaunchExplainerModal
            isOpen={showFirstLaunchModal}
            onClose={handleCloseFirstLaunchModal}
            onCreateVybe={() => setShowCreateEventModal(true)}
          />
        </Suspense>
      )}

    </div>
  );
};

function App() {
  console.log('VITE_ONBOARDING_JOURNEY value at runtime:', import.meta.env.VITE_ONBOARDING_JOURNEY);

  // ✅ Provider order: OverlayProvider -> OnboardingCoach -> AppSheets
  return (
    <OverlayProvider>
      <OnboardingCoachProvider>
        <AppSheetsProvider>
          <ScrollToTop />
          <Routes>
            <Route path="*" element={<ProtectedRoutes />} />
          </Routes>
        </AppSheetsProvider>
      </OnboardingCoachProvider>
    </OverlayProvider>
  );
}

export default App;
